import type { Delivery, IDeliveryLine } from '@interfaces/ILogistics';
import { HandshakeIcon, PackageIcon } from '@utils/icons';
import { lineAmount } from '../lines';
import { FACE_LINES, type CardTranslate } from './boardCard.logic';
import { DeliverySourcePill } from './DeliverySource';
import styles from './DeliveryBoard.module.css';

interface ICardFaceProps {
  delivery: Delivery;
  /** Every source the face speaks for — the Scheduler pill's tooltip. */
  sources: IDeliveryLine['sources'];
  t: CardTranslate;
}

/**
 * The card's identity rows: the ref and destination, then up to two lines in
 * full and "+ N more material(s)" — the same shape for one line as for
 * several, so a column reads as one list of trucks. (No "not yet synced" mark:
 * the board's Delivered column now holds only unsynced arrivals, and says so.)
 */
export function CardFace({ delivery, sources, t }: ICardFaceProps) {
  return (
    <>
      <span className={styles.materialLine}>
        <span className={styles.material}>
          #{delivery.ref} · {delivery.destinationName}
        </span>
        {/* The tooltip names the material per task only when there are
            several to tell apart — on a one-line truck it would just repeat
            the line underneath. */}
        {sources.length > 0 ? (
          <DeliverySourcePill sources={sources} lines={delivery.lines.length > 1 ? delivery.lines : undefined} />
        ) : null}
      </span>
      {delivery.lines.slice(0, FACE_LINES).map((line) => (
        <span key={line.id} className={styles.lineRow} data-testid="card-line">
          <span className={styles.fieldIcon} aria-hidden="true">
            <PackageIcon size={12} />
          </span>
          <span className={styles.lineName}>{line.itemName}</span>
          {lineAmount(line) !== null ? <span className={styles.qty}>{lineAmount(line, t)}</span> : null}
        </span>
      ))}
      {delivery.lines.length > FACE_LINES ? (
        <span className={styles.moreLines}>
          {t('logistics.pipeline.moreMaterials', { count: delivery.lines.length - FACE_LINES })}
        </span>
      ) : null}
    </>
  );
}

interface ISupplierLineProps {
  name: string;
}

/** The order's supplier, one muted row under the card's lines, marked with
 *  the company icon the way each line carries the material one. */
export function SupplierLine({ name }: ISupplierLineProps) {
  return (
    // A material line's row, so its icon lines up with theirs.
    <span className={styles.lineRow}>
      <span className={styles.fieldIcon} aria-hidden="true">
        <HandshakeIcon size={12} />
      </span>
      <span className={styles.meta} data-testid="card-supplier">
        {name}
      </span>
    </span>
  );
}
